import { useMutation, useQueryClient } from '@tanstack/react-query';
//
import { Box, MenuItem, TableCell, TableRow, Typography } from '@mui/material';
// components
import { Iconify } from '@/components';
import DialogSimple from '@/components/dialog/DialogSimple';
import { TableMoreMenu, TableRowLink } from '@/components/table';
import ProviderCityPriceDialog from './ProviderCityPriceDialog';
// api
import { deleteProviderCityPrice } from '../api';
// hooks + utils
import useToggle from '@/hooks/useToggle';
import { use3DotMenu } from '@/hooks/use3DotMenu';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { fCurrency } from '@/utils/formatNumber';
import { fDateAbbrevMonth } from '@/utils/formatTime';
import { servicesManagementPath } from '@/features/services-management';
import {
  getProviderCityPriceCityName,
  getProviderCityPriceServiceName,
  getProviderCityPriceVariantName,
  isProviderCityPriceExpired,
} from '../utils';
// types
import { ProviderCityPrice } from '../types';

type ProviderCityPricesTableRowProps = {
  row: ProviderCityPrice;
  canUpdate: boolean;
  canDelete: boolean;
};

export default function ProviderCityPricesTableRow({
  row,
  canUpdate,
  canDelete,
}: ProviderCityPricesTableRowProps) {
  const { errorMsg, successMsg } = useSnackbarMsg();
  const queryClient = useQueryClient();

  const {
    id,
    service_variant,
    price,
    sun_bank_holiday_extra,
    start_date,
    end_date,
  } = row;

  const isExpired = isProviderCityPriceExpired(row);
  const isEditAllowed = canUpdate && !isExpired;
  const isDeleteAllowed = canDelete;

  const {
    toggle: isPriceDialogOpen,
    onOpen: openPriceDialog,
    onClose: closePriceDialog,
  } = useToggle();

  const {
    toggle: isDeleteDialogOpen,
    onOpen: openDeleteDialog,
    onClose: closeDeleteDialog,
  } = useToggle();

  const { openMenu, handleOpenMenu, handleCloseMenu } = use3DotMenu();

  const deleteMutation = useMutation({
    mutationFn: () => deleteProviderCityPrice(String(id)),
    onSuccess: () => {
      queryClient.invalidateQueries(['providerCityPrices']);
      successMsg('Provider price successfully deleted');
      closeDeleteDialog();
    },
    onError: (error) =>
      errorMsg(error, `Something went wrong while deleting provider price`),
  });

  const handleEditRow = () => {
    openPriceDialog();
    handleCloseMenu();
  };

  const handleDeleteRow = () => {
    deleteMutation.mutate();
  };

  return (
    <>
      <TableRow
        hover
        sx={{
          color: (theme) => (isExpired ? theme.palette.grey[400] : 'inherit'),
        }}
      >
        <TableCell width={10} />

        <TableCell
          align="left"
          sx={{
            color: 'inherit',
            paddingLeft: isExpired ? '1.5rem' : 'none',
          }}
        >
          {isEditAllowed ? (
            <TableRowLink
              linkText={getProviderCityPriceCityName(row)}
              linkTo="#"
              onClick={handleEditRow}
            />
          ) : (
            <>{getProviderCityPriceCityName(row)}</>
          )}
        </TableCell>

        <TableCell align="left" sx={{ color: 'inherit' }}>
          {service_variant.service ? (
            <TableRowLink
              linkText={getProviderCityPriceServiceName(row)}
              linkTo={servicesManagementPath('editService', {
                serviceId: String(service_variant.service.id),
              })}
              sx={{ color: 'inherit' }}
            />
          ) : (
            getProviderCityPriceServiceName(row)
          )}
        </TableCell>

        <TableCell align="left" sx={{ color: 'inherit' }}>
          {service_variant ? (
            <TableRowLink
              linkText={getProviderCityPriceVariantName(row)}
              linkTo={servicesManagementPath('editVariant', {
                variantId: String(service_variant.id),
                tabSid: 'details',
              })}
              sx={{ color: 'inherit' }}
            />
          ) : (
            getProviderCityPriceVariantName(row)
          )}
        </TableCell>

        <TableCell align="left" sx={{ color: 'inherit' }}>
          {fCurrency(price)}
        </TableCell>

        <TableCell align="left" sx={{ color: 'inherit' }}>
          {fCurrency(sun_bank_holiday_extra)}
        </TableCell>

        <TableCell align="left" sx={{ color: 'inherit' }}>
          {start_date
            ? fDateAbbrevMonth(start_date, 'EEE, dd MMM yyyy')
            : 'N/A'}
        </TableCell>

        <TableCell align="left" sx={{ color: 'inherit' }}>
          {end_date ? fDateAbbrevMonth(end_date, 'EEE, dd MMM yyyy') : 'N/A'}
        </TableCell>

        <TableCell align="right">
          {(isEditAllowed || isDeleteAllowed) && (
            <TableMoreMenu
              open={openMenu}
              onOpen={handleOpenMenu}
              onClose={handleCloseMenu}
              arrow="top-right"
              actions={
                <>
                  {isEditAllowed && (
                    <MenuItem onClick={handleEditRow}>
                      <Iconify icon="eva:edit-outline" />
                      Edit
                    </MenuItem>
                  )}
                  {isDeleteAllowed && (
                    <MenuItem
                      onClick={() => {
                        openDeleteDialog();
                        handleCloseMenu();
                      }}
                      sx={{ color: 'error.main' }}
                    >
                      <Iconify icon="eva:trash-2-outline" />
                      Delete
                    </MenuItem>
                  )}
                </>
              }
            />
          )}
        </TableCell>
      </TableRow>

      <DialogSimple
        dialogTitle="Delete provider price"
        open={isDeleteDialogOpen}
        onClickButtonClose={closeDeleteDialog}
        onClickButtonLoading={handleDeleteRow}
        loadingButton={true}
        disagreeButton={true}
        loadingButtonText="Delete"
        loadingButtonColor="error"
        disagreeButtonText="Cancel"
        disagreeButtonColor="inherit"
      >
        <Box sx={{ px: 3 }}>
          <Typography sx={{ mt: 1 }}>
            Are you sure you want to delete this provider price:{' '}
            <span style={{ fontWeight: 600 }}>
              {getProviderCityPriceCityName(row)}: {fCurrency(price)}
            </span>
            ?
          </Typography>
        </Box>
      </DialogSimple>

      <ProviderCityPriceDialog
        isOpen={isPriceDialogOpen}
        onClose={closePriceDialog}
        currentPriceId={String(id)}
      />
    </>
  );
}
